import { SplitTestsState } from '../../../tabs/CampaignWorkflowTab/state/SplitTests/SplitTestsState';
import {
  SubjectLineRequiredRule,
  SendEmailTemplateRequiredRule,
  IpAddressOnlyRequiredForDisplayWebContentRule,
  FrequencyRequiredRule,
  DisplayWebContentTemplateRequiredRule,
  SendEmailRequiredWithSMBRule,
  AtLeastOneTriggerRequiredRule,
  AtLeastOneExitIntentRequiredRule,
  InactivityRequiredIfSelectedRule,
  AtLeastOneMessageRequiredRule,
  CampaignNameRequiredRule,
  NameRequiredRule,
  DisplayWebContentNameRequiredRule,
  SendEmailNameRequiredRule,
  StartDateRequiredRule,
  AnyDatesCantBeInThePastRule,
  EmbeddedPositionRequiredRule,
  EmbeddedSelectorRequiredRule,
} from '../Rules';
import { AtLeastOneMessageValidTriggerRequiredRule } from '../Rules/Trigger/AtLeastOneMessageValidTriggerRequiredRule';
import { OnClickSelectorRequiredRule } from '../Rules/Trigger/OnClickSelectorRequiredRule';
import { MessageOnClickSelectorRequiredRule } from '../Rules/Trigger/MessageOnClickSelectorRequiredRule';
import { CampaignInfo, DomainRule, FlagService, IsInFlowService } from '../types';
import { ValidationStrategy } from './ValidationStrategy';
import { SendEmailMessage, Trigger, WebsiteContentMessage } from 'sections/onsite-campaigns/types';

const PASSED = [];

export class TestModeStrategy implements ValidationStrategy {
  public constructor(
    private inFlowService: IsInFlowService,
    private flagService: FlagService,
    private campaignInfo: CampaignInfo,
    private trigger: Trigger,
    private onsiteMessages: WebsiteContentMessage[],
    private offsiteMessages: SendEmailMessage[],
    private splitTests: SplitTestsState,
    private branchNames: string[],
  ) {}

  private branchValidationChecks() {
    if (!this.inFlowService.isBranchingEnabled()) {
      return PASSED;
    }

    const rules = [NameRequiredRule({ names: this.branchNames })];

    return rules.filter(s => s.result === 'fail');
  }

  private sendEmailValidationChecks() {
    if (!this.inFlowService.isSendEmailInFlow()) {
      return PASSED;
    }

    /*
      If there are no offsite messages then force the validation to fail.
    */
    if (!this.offsiteMessages?.length) {
      return [
        SubjectLineRequiredRule({ subjectLine: undefined }),
        SendEmailTemplateRequiredRule({ template: undefined }),
        SendEmailNameRequiredRule({ name: '' }),
      ];
    }

    const rules = this.offsiteMessages.flatMap(s => {
      return [
        SubjectLineRequiredRule({ subjectLine: s.subjectLine }),
        SendEmailTemplateRequiredRule({ template: s.template }),
        SendEmailNameRequiredRule({ name: s.name }),
      ];
    });

    return rules.filter(s => s.result === 'fail');
  }

  private splitTestValidationChecks() {
    return Object.keys(this.splitTests)
      .map(k => this.splitTests[k])
      .flatMap(s => {
        return [StartDateRequiredRule(s.scheduling), AnyDatesCantBeInThePastRule(s.scheduling)];
      })
      .filter(s => s.result === 'fail');
  }

  private displayWebContentValidationChecks() {
    if (!this.inFlowService.isDisplayWebsiteContentInFlow()) {
      return PASSED;
    }

    let rules = [
      IpAddressOnlyRequiredForDisplayWebContentRule({
        allowList: this.campaignInfo.allowList,
        isInFlowService: this.inFlowService,
      }),
    ];

    /*
      If there are no onsite messages then force the validation to fail.
    */
    if (!this.onsiteMessages?.length) {
      return [
        DisplayWebContentTemplateRequiredRule({ template: undefined }),
        DisplayWebContentNameRequiredRule({ name: '' }),
        SendEmailRequiredWithSMBRule({
          displayWebsiteContentTemplate: undefined,
          isInFlowService: this.inFlowService,
        }),
      ];
    }

    const messageResults = this.onsiteMessages.flatMap(s => {
      const flags = this.flagService.getEnabledFlags();

      const conditionalRules = [];
      if (flags.isEmbeddedContentEnabled && s.template?.isEmbedded) {
        conditionalRules.push(EmbeddedPositionRequiredRule({ position: s.embeddedPosition }));
        conditionalRules.push(EmbeddedSelectorRequiredRule({ selector: s.embeddedSelector }));
      }

      return [
        DisplayWebContentTemplateRequiredRule({ template: s.template }),
        DisplayWebContentNameRequiredRule({ name: s.name }),
        SendEmailRequiredWithSMBRule({
          displayWebsiteContentTemplate: s.template,
          isInFlowService: this.inFlowService,
        }),
        MessageOnClickSelectorRequiredRule({ triggers: s.triggers }),
        ...conditionalRules,
      ];
    });

    rules = [...rules, ...messageResults];

    return rules.filter(s => s.result === 'fail');
  }

  private triggerValidationChecks() {
    const rules: DomainRule[] = [];
    const campaignRules: DomainRule[] = [];
    rules.push(FrequencyRequiredRule({ frequency: this.trigger.frequency }));
    rules.push(
      OnClickSelectorRequiredRule({ selectedTriggers: this.trigger.selectedTriggers, onClick: this.trigger.onClick }),
    );

    const messageValidationRules = this.onsiteMessages.map(message => {
      return AtLeastOneMessageValidTriggerRequiredRule({
        onsiteMessage: message,
      });
    });
    const hasFailedMessageValidation = messageValidationRules.filter(s => s.result === 'fail').length;

    if (hasFailedMessageValidation) {
      campaignRules.push(AtLeastOneTriggerRequiredRule({ selectedTriggers: this.trigger.selectedTriggers }));
      campaignRules.push(
        AtLeastOneExitIntentRequiredRule({
          selectedTriggers: this.trigger.selectedTriggers,
          exitIntentAreas: this.trigger.exitIntentAreas,
        }),
      );

      campaignRules.push(
        InactivityRequiredIfSelectedRule({
          selectedTriggers: this.trigger.selectedTriggers,
          inactivity: this.trigger.inactivity,
        }),
      );
    }
    const hasCampaignValidationFailed = campaignRules.filter(s => s.result === 'fail').length;
    if (hasFailedMessageValidation && !hasCampaignValidationFailed) {
      return [...rules, ...campaignRules].filter(s => s.result === 'fail');
    }
    return [...rules, ...messageValidationRules].filter(s => s.result === 'fail');
  }

  private standardChecks() {
    const rules = [
      AtLeastOneMessageRequiredRule({ isInFlowService: this.inFlowService }),
      CampaignNameRequiredRule({ name: this.campaignInfo.name }),
    ];

    return rules.filter(s => s.result === 'fail');
  }

  public validate(): DomainRule[] {
    return [
      ...this.standardChecks(),
      ...this.triggerValidationChecks(),
      ...this.displayWebContentValidationChecks(),
      ...this.splitTestValidationChecks(),
      ...this.sendEmailValidationChecks(),
      ...this.branchValidationChecks(),
    ];
  }
}
